/**
 * Page border and vertical scroll indicator.
 * Draws a fixed border around the viewport (via `.page-border` CSS).
 * On desktop, shows a clickable/keyboard-navigable block-character
 * progress bar on the left edge that reflects and controls scroll position.
 */
import { useScroll, useMotionValueEvent } from "framer-motion";
import { useState, useCallback } from "react";

const PageFrame = () => {
  const { scrollYProgress } = useScroll();
  const [progress, setProgress] = useState(0);

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    setProgress(latest);
  });

  const handleClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickY = e.clientY - rect.top;
    const percentage = clickY / rect.height;
    
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    window.scrollTo({
      top: percentage * maxScroll,
      behavior: "smooth"
    });
  }, []);

  // Generate the block pattern (░ for unscrolled, █ for scrolled)
  const totalBlocks = 12;
  const filledBlocks = Math.round(progress * totalBlocks);

  return (
    <>
      {/* Page border */}
      <div className="page-border" />
      
      {/* Vertical scroll indicator - hidden on mobile */}
      <div 
        className="fixed left-8 top-1/2 -translate-y-1/2 z-[9998] cursor-pointer select-none hidden md:block"
        role="slider"
        aria-label="Page scroll position"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress * 100)}
        tabIndex={0}
        onClick={handleClick}
        onKeyDown={(e) => {
          const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
          if (e.key === 'ArrowDown') { window.scrollBy({ top: maxScroll / totalBlocks, behavior: 'smooth' }); e.preventDefault(); }
          if (e.key === 'ArrowUp') { window.scrollBy({ top: -maxScroll / totalBlocks, behavior: 'smooth' }); e.preventDefault(); }
        }}
        style={{ height: 120 }}
      >
        <div className="flex flex-col h-full justify-between font-mono text-xs text-foreground leading-none">
          {Array.from({ length: totalBlocks }).map((_, i) => (
            <span 
              key={i} 
              className="transition-opacity duration-150"
              style={{ opacity: i < filledBlocks ? 1 : 0.25 }}
            >
              {i < filledBlocks ? "█" : "░"}
            </span>
          ))}
        </div>
      </div>
    </>
  );
};

export default PageFrame;
